import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { useMutateShiplistContext } from './MutateShiplistContext';
import { useLocalization } from '@ais-ui/locale-management';

import { ValidationIndices } from './shiplistTypes';

export type { ValidationIndices } from './shiplistTypes';

interface ShiplistConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const formatIndices = (indices: number[]) => {
  if (indices.length === 0) return '';
  if (indices.length === 1) return (indices[0] + 1).toString();

  const displayIndices = indices.slice(0, 10).map((index) => index + 1);
  const formattedIndices =
    displayIndices.length === 2
      ? displayIndices.join(' and ')
      : displayIndices.slice(0, -1).join(', ') +
        ' and ' +
        displayIndices[displayIndices.length - 1];

  if (indices.length > 10) {
    return `${formattedIndices} and ${indices.length - 10} more`;
  }

  return formattedIndices;
};

export const ShiplistConfirmationDialog = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
}: ShiplistConfirmationDialogProps) => {
  const { invalidIndices } = useMutateShiplistContext();
  const { messages } = useLocalization();

  const { imo: invalidImos, mmsi: invalidMmsis } = invalidIndices;

  const dialogTexts = useMemo(() => {
    const hasImoErrors = invalidImos.length > 0;
    const hasMmsiErrors = invalidMmsis.length > 0;

    if (!hasImoErrors && !hasMmsiErrors) return [];

    const parts: string[] = [];

    // IMO errors
    if (hasImoErrors) {
      const imoText =
        invalidImos.length === 1
          ? `Ship ${invalidImos[0] + 1} ${messages.shiplistsConfirmationHasInvalidImoNumberText}`
          : `Ships ${formatIndices(invalidImos)} ${messages.shiplistsConfirmationHaveInvalidImoNumberText}`;
      parts.push(imoText);
    }

    // MMSI errors
    if (hasMmsiErrors) {
      const mmsiText =
        invalidMmsis.length === 1
          ? `Ship ${invalidMmsis[0] + 1} ${messages.shiplistsConfirmationHasInvalidMmsiNumberText}`
          : `Ships ${formatIndices(invalidMmsis)} ${messages.shiplistsConfirmationHaveInvalidMmsiNumberText}`;
      parts.push(mmsiText);
    }

    return parts;
  }, [invalidImos, invalidMmsis]);

  const alertDialogTitle = useMemo(() => {
    const hasImoErrors = invalidImos.length > 0;
    const hasMmsiErrors = invalidMmsis.length > 0;

    if (hasImoErrors && hasMmsiErrors) {
      return messages.shiplistsConfirmationHasInvalidImoAndMmsiErrorsValue;
    } else if (hasImoErrors) {
      return messages.shiplistsConfirmationHasImoErrorsValue;
    } else if (hasMmsiErrors) {
      return messages.shiplistsConfirmationHasMmsiErrorsValue;
    } else {
      return '';
    }
  }, [invalidImos.length, invalidMmsis.length]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alertDialogTitle}</AlertDialogTitle>
          <AlertDialogDescription className={'flex flex-col space-y-1'}>
            {dialogTexts.map((text, index) => (
              <p key={index}>{text}</p>
            ))}
            <p>{messages.shiplistsConfirmationDialogWarning}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {messages.shiplistsConfirmationAlertDialogCancel}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            {messages.shiplistsConfirmationAlertDialogContinue}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
